/*
 * The map rank map (maprank.php's map, rebuilt on the same Google map as VisitMap): one dot per spot checked, colored by
 * where our listing came in when someone searched from there, with the number on it. Click a dot to see the top 3 there.
 * Our listing shows as a pin.
 *
 * In setup the dots are gray and show the area before it is checked. The middle of an area is its listing's own pin, the
 * one Google has for the profile; drag that pin to move the area (owner, 2026-11-02: "the google profile will have a pin
 * for where the address is or profile is, thats the center, we can move that if we want to"). Click a dot to turn it off
 * (water, no homes) or on again. A click or double click on the map itself only moves or zooms the map, so
 * zooming never undoes the spots turned off (owner, 2026-11-02: "when i double click to zoom out it ticks back the ones i
 * unticked"). Each listing's area is a circle; the one being set is green, the others gray.
 *
 * Our Google listings show as name pins (owner, 2026-11-02: "since i have 2").
 *
 * With no Google Maps key (the click-through preview, a PC with no key) or a refused one, the same dots show as a square
 * of cells, so the numbers and clicks still work.
 */
import { useEffect, useRef, useState } from "react";
import { useMapsKey } from "../api/hooks";
import { loadGoogleMaps, onMapsRefused } from "./googleMaps";
import { MAP_ID } from "./VisitMap";

export interface RankDot {
  /** The spot's place in the full square, row by row from the north. */
  i: number;
  lat: number;
  lng: number;
  /** Our place there; null past 20. Undefined while setting up (a gray dot). */
  rank?: number | null;
  /** Turned off (setup only): drawn hollow. */
  off?: boolean;
}

/** The color band of a place: the top 3 most people call, then the rest of page 1, then lower. */
export const rankBand = (r: number | null | undefined) =>
  r === undefined ? "plan" : r === null || r < 1 ? "miss" : r <= 3 ? "top" : r <= 10 ? "mid" : "low";
export const rankWord = (r: number | null | undefined) => (r === undefined ? "" : r === null || r < 1 ? "20+" : String(r));

function dark() {
  const t = document.documentElement.dataset.theme;
  return t === "dark" || (t !== "light" && window.matchMedia("(prefers-color-scheme: dark)").matches);
}

export function RankMap({
  center,
  dots,
  pins = [],
  circles = [],
  picked = null,
  onDot,
  onMove,
  label,
}: {
  center: { lat: number; lng: number };
  dots: RankDot[];
  /** Our Google listings, as name pins. A pin with drag is the middle of the area being set: dragging it moves the area. */
  pins?: { lat: number; lng: number; name: string; drag?: boolean }[];
  /** Circles drawn around areas while setting up, in miles from their middle; the active one is green. */
  circles?: { lat: number; lng: number; miles: number; active?: boolean }[];
  picked?: number | null;
  onDot?: (i: number) => void;
  /** Setup: where the dragged listing pin is let go is the new middle. */
  onMove?: (at: { lat: number; lng: number }) => void;
  label: string;
}) {
  const box = useRef<HTMLDivElement>(null);
  const map = useRef<google.maps.Map | null>(null);
  const drawn = useRef<{ pins: google.maps.marker.AdvancedMarkerElement[]; edges: google.maps.Circle[] }>({
    pins: [],
    edges: [],
  });
  // The newest handlers, so the map's own listeners (made once) call the current ones.
  const handlers = useRef({ onDot, onMove });
  handlers.current = { onDot, onMove };
  // The last dot clicked and when: the second click of a double click is not a second toggle.
  const lastDot = useRef({ i: -1, at: 0 });
  // Where the dots are, to frame them once the map has its size.
  const bounds = useRef<google.maps.LatLngBounds | null>(null);
  const mapsKey = useMapsKey();
  const key = mapsKey.data?.key ?? null;
  const [ready, setReady] = useState(false);
  const [problem, setProblem] = useState(false);

  useEffect(() => {
    if (!key || !box.current) return;
    const el = box.current;
    let gone = false;
    let unobserve: () => void = () => undefined;
    const noPageZoom = (e: Event) => e.preventDefault();
    el.addEventListener("gesturestart", noPageZoom);
    const stop = onMapsRefused(() => setProblem(true));
    loadGoogleMaps(key)
      .then(async () => {
        const { Map } = (await google.maps.importLibrary("maps")) as google.maps.MapsLibrary;
        await google.maps.importLibrary("marker");
        if (gone) return;
        const m = new Map(el, {
          mapId: MAP_ID,
          center,
          zoom: 10,
          colorScheme: dark() ? google.maps.ColorScheme.DARK : google.maps.ColorScheme.LIGHT,
          gestureHandling: "cooperative",
          streetViewControl: false,
          mapTypeControl: false,
          clickableIcons: false,
        });
        // Framed again when the box first gets its size or changes it (a window opening, a phone turning).
        const sized = new ResizeObserver(() => {
          if (bounds.current) m.fitBounds(bounds.current, 24);
        });
        sized.observe(el);
        unobserve = () => sized.disconnect();
        // Dots shrink when zoomed far out, so a big area does not turn into one blob.
        const fit = () => {
          const z = m.getZoom() ?? 10;
          el.style.setProperty("--dot-scale", String(z >= 12 ? 1 : z >= 11 ? 0.85 : z >= 10 ? 0.72 : 0.6));
        };
        fit();
        m.addListener("zoom_changed", fit);
        map.current = m;
        setReady(true);
      })
      .catch(() => {
        if (!gone) setProblem(true);
      });
    return () => {
      gone = true;
      unobserve();
      stop();
      el.removeEventListener("gesturestart", noPageZoom);
      map.current = null;
      setReady(false);
    };
  }, [key]);

  const look =
    dots.map((d) => `${String(d.i)}:${String(d.lat)},${String(d.lng)}:${String(d.rank)}:${d.off ? 1 : 0}`).join("|") +
    `|${String(picked)}|${JSON.stringify(pins)}|${JSON.stringify(circles)}|${String(center.lat)},${String(center.lng)}`;

  useEffect(() => {
    const m = map.current;
    if (!m || !ready) return;
    const was = drawn.current;
    for (const p of was.pins) p.map = null;
    for (const e of was.edges) e.setMap(null);
    const now: typeof was = { pins: [], edges: [] };
    for (const c of circles)
      now.edges.push(
        new google.maps.Circle({
          map: m,
          center: { lat: c.lat, lng: c.lng },
          radius: c.miles * 1609.34,
          clickable: false,
          strokeColor: c.active ? "#2e7d32" : "#8a94a3",
          strokeOpacity: 0.8,
          strokeWeight: 2,
          fillColor: c.active ? "#2e7d32" : "#8a94a3",
          fillOpacity: c.active ? 0.05 : 0.02,
        }),
      );
    for (const d of dots) {
      // Built as text, never HTML.
      const dot = document.createElement("span");
      dot.className = `rank-dot ${d.off ? "off" : rankBand(d.rank)}${picked === d.i ? " picked" : ""}`;
      dot.textContent = d.off ? "" : rankWord(d.rank);
      dot.setAttribute(
        "aria-label",
        d.off
          ? "Spot turned off"
          : d.rank === undefined
            ? "Spot to check"
            : d.rank === null
              ? "Not in the top 20 here"
              : `Number ${String(d.rank)} here`,
      );
      const marker = new google.maps.marker.AdvancedMarkerElement({
        map: m,
        position: { lat: d.lat, lng: d.lng },
        content: dot,
        zIndex: picked === d.i ? 1000 : 10,
        gmpClickable: !!handlers.current.onDot,
      });
      marker.addEventListener("gmp-click", () => {
        const was = lastDot.current;
        lastDot.current = { i: d.i, at: Date.now() };
        if (was.i === d.i && Date.now() - was.at < 400) return;
        handlers.current.onDot?.(d.i);
      });
      now.pins.push(marker);
    }
    for (const h of pins) {
      const drag = !!h.drag && !!handlers.current.onMove;
      const pin = document.createElement("span");
      pin.className = `rank-home${drag ? " drag" : ""}`;
      pin.textContent = h.name;
      const marker = new google.maps.marker.AdvancedMarkerElement({
        map: m,
        position: { lat: h.lat, lng: h.lng },
        content: pin,
        zIndex: drag ? 1002 : 1001,
        title: drag ? `${h.name}. Drag to move its area.` : h.name,
        gmpDraggable: drag,
      });
      if (drag)
        marker.addListener("dragend", () => {
          const at = marker.position;
          if (!at) return;
          handlers.current.onMove?.(at instanceof google.maps.LatLng ? at.toJSON() : { lat: at.lat, lng: at.lng });
        });
      now.pins.push(marker);
    }
    drawn.current = now;
  }, [look, ready]);

  // Frame the area when it moves or grows, not on every click, so a map someone zoomed stays put.
  const frame = `${String(center.lat)},${String(center.lng)},${JSON.stringify(circles.map((c) => c.miles))},${String(dots.length)}`;
  useEffect(() => {
    const m = map.current;
    if (!m || !ready || dots.length === 0) return;
    const b = new google.maps.LatLngBounds();
    for (const d of dots) b.extend(d);
    bounds.current = b;
    m.fitBounds(b, 24);
  }, [frame, ready]);

  if (!key || problem)
    return (
      <div className="rank-map">
        <Square dots={dots} picked={picked} onDot={onDot} label={label} />
        {mapsKey.data && !key ? (
          <p className="map-note">The map needs a Google Maps key. Head office adds it in Settings, Connected services.</p>
        ) : problem ? (
          <p className="map-note">The Google map did not load, so the spots show as a square.</p>
        ) : null}
      </div>
    );
  return (
    <div className="rank-map">
      <div ref={box} className={`map-box${onMove ? " pick" : ""}`} role="region" aria-label={label} />
    </div>
  );
}

/** The spots as a square of cells, north at the top, laid out by their own rows and columns: what shows with no map. */
function Square({
  dots,
  picked,
  onDot,
  label,
}: {
  dots: RankDot[];
  picked: number | null;
  onDot?: ((i: number) => void) | undefined;
  label: string;
}) {
  const lats = [...new Set(dots.map((d) => d.lat.toFixed(5)))].sort((a, b) => Number(b) - Number(a));
  const lngs = [...new Set(dots.map((d) => d.lng.toFixed(5)))].sort((a, b) => Number(a) - Number(b));
  const size = Math.max(lats.length, lngs.length, 1);
  // A row or column with no spot at all (the edge of a circle) still keeps its place.
  const rowOf = (lat: number) => lats.indexOf(lat.toFixed(5)) + Math.floor((size - lats.length) / 2);
  const colOf = (lng: number) => lngs.indexOf(lng.toFixed(5)) + Math.floor((size - lngs.length) / 2);
  const at = new Map(dots.map((d) => [rowOf(d.lat) * size + colOf(d.lng), d]));
  return (
    <div className="rank-square" style={{ gridTemplateColumns: `repeat(${String(size)}, 1fr)` }} aria-label={label} role="group">
      {Array.from({ length: size * size }, (_v, c) => {
        const d = at.get(c);
        if (!d) return <span key={c} className="rank-cell none" />;
        const i = d.i;
        return (
          <button
            key={c}
            type="button"
            className={`rank-cell ${d.off ? "off" : rankBand(d.rank)}${picked === i ? " picked" : ""}`}
            disabled={!onDot}
            onClick={() => onDot?.(i)}
            aria-label={d.off ? "Spot turned off" : d.rank === undefined ? "Spot to check" : `Spot, ${rankWord(d.rank)}`}
          >
            {d.off ? "" : rankWord(d.rank)}
          </button>
        );
      })}
    </div>
  );
}
